import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import {
  AuthAlert,
  AuthField,
  AuthLayout,
  AuthSubmit,
  AuthSuccess,
  PasswordStrength,
} from "./AuthLayout";

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const pwError =
    touched && password.length < 8 ? "Password must be at least 8 characters." : null;
  const confirmError = touched && confirm !== password ? "Passwords don't match." : null;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    setError(null);
    if (password.length < 8 || confirm !== password) return;
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      // Успех показываем здесь, а не редиректом на /login: человек
      // должен увидеть, что пароль действительно сменился, прежде чем
      // его просят войти заново.
      setDone(true);
    } catch {
      setError("This link has expired or has already been used.");
    } finally {
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <AuthLayout
        title="Set a new password"
        subtitle="Choose a new password for your account."
      >
        <AuthAlert>
          This reset link is malformed.{" "}
          <Link to="/forgot-password">Request a new one.</Link>
        </AuthAlert>
        <div className="au-links">
          <Link to="/login">Back to log in</Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Set a new password"
      subtitle="Choose a new password for your account."
    >
      {done ? (
        <AuthSuccess
          title="Password updated"
          cta={
            <Link className="btn primary au-go" to="/login">
              Log in
            </Link>
          }
        >
          <p>You can now log in with your new password.</p>
        </AuthSuccess>
      ) : (
        <>
          {error && (
            <AuthAlert>
              {error} <Link to="/forgot-password">Request a new one.</Link>
            </AuthAlert>
          )}
          <form className="au-card" onSubmit={onSubmit} noValidate>
            <AuthField
              id="reset-password"
              label="New password"
              type="password"
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              disabled={submitting}
              error={pwError}
            >
              <PasswordStrength value={password} />
            </AuthField>
            <AuthField
              id="reset-confirm"
              label="Confirm password"
              type="password"
              value={confirm}
              onChange={setConfirm}
              autoComplete="new-password"
              disabled={submitting}
              error={confirmError}
            />
            <AuthSubmit busy={submitting} busyLabel="Saving">
              Set new password
            </AuthSubmit>
          </form>
        </>
      )}
      <div className="au-links">
        <Link to="/login">Back to log in</Link>
      </div>
    </AuthLayout>
  );
}
